import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { errorText } from '../../lib/apiErrors';
import { ROLL_GRACE, revokeKey, rollKey } from '../../lib/developerApi';

const SECONDARY = 'h-9 px-4 rounded-md border border-stone-200 text-sm font-medium inline-flex items-center gap-2 hover:bg-stone-50';

/**
 * Roll: a new secret for the same key id, scopes and history. The old secret
 * keeps working for the chosen grace so an integration can be updated without a
 * gap. On success the new raw key goes to `onRolled` for `RevealKeyDialog`.
 */
export function RollKeyDialog({ apiKey, headers, onClose, onRolled }) {
    const [grace, setGrace] = useState('24h');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setGrace('24h');
    }, [apiKey?.key_id]);

    if (!apiKey) return null;

    const submit = async () => {
        setSaving(true);
        try {
            const rolled = await rollKey(headers, apiKey.key_id, grace);
            onRolled(rolled, ROLL_GRACE.find((g) => g.value === grace)?.label);
        } catch (err) {
            toast.error(errorText(err, 'Could not roll the key.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-lg" data-testid="roll-key-dialog">
                <DialogHeader>
                    <DialogTitle>Roll “{apiKey.name}”</DialogTitle>
                    <DialogDescription>Get a new secret with the same name and permissions.</DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium text-stone-900">Keep the old key working for</span>
                    <div className="flex items-center gap-1 p-1 bg-stone-100 rounded-lg">
                        {ROLL_GRACE.map((g) => (
                            <button
                                key={g.value}
                                type="button"
                                data-testid={`grace-${g.value}`}
                                aria-pressed={grace === g.value}
                                onClick={() => setGrace(g.value)}
                                className={`flex-1 h-7 rounded-md text-[13px] font-medium whitespace-nowrap px-2 ${grace === g.value ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-600'}`}
                            >
                                {g.label}
                            </button>
                        ))}
                    </div>
                    <span className="text-xs text-stone-500">Time to paste the new key into Zapier or your website before the old one stops.</span>
                </div>
                <DialogFooter className="gap-2">
                    <button type="button" className={SECONDARY} onClick={onClose}>Cancel</button>
                    <button
                        type="button"
                        data-testid="roll-key-submit"
                        disabled={saving}
                        onClick={submit}
                        className="h-9 px-4 rounded-md bg-stone-900 text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-stone-800 disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        Roll key
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/** Revoke: immediate and final. States the impact (last 24 h of requests). */
export function RevokeKeyDialog({ apiKey, headers, onClose, onRevoked, onRollInstead }) {
    const [saving, setSaving] = useState(false);
    if (!apiKey) return null;

    const submit = async () => {
        setSaving(true);
        try {
            const revoked = await revokeKey(headers, apiKey.key_id);
            toast.success(`“${apiKey.name}” revoked`);
            onRevoked(revoked);
        } catch (err) {
            toast.error(errorText(err, 'Could not revoke the key.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-lg" data-testid="revoke-key-dialog">
                <DialogHeader>
                    <DialogTitle>Revoke “{apiKey.name}”?</DialogTitle>
                    <DialogDescription>This can’t be undone.</DialogDescription>
                </DialogHeader>
                <p className="text-sm text-stone-700 leading-relaxed">
                    Anything using <span className="font-mono text-[12.5px]">{apiKey.display}</span> stops working on its next request and
                    gets a <span className="font-mono text-[12.5px]">401 invalid_key</span>. It sent{' '}
                    <b data-testid="revoke-impact">{(apiKey.requests_24h || 0).toLocaleString()} requests</b> in the last 24 hours.
                </p>
                <p className="text-[13.5px] text-stone-500">
                    Records it already created stay in the CRM. If you only need a new secret, roll the key instead.
                </p>
                <DialogFooter className="gap-2">
                    <button type="button" className={SECONDARY} onClick={onClose}>Cancel</button>
                    <button type="button" className={SECONDARY} onClick={onRollInstead}>
                        <RefreshCw className="w-3.5 h-3.5" /> Roll instead
                    </button>
                    <button
                        type="button"
                        data-testid="revoke-key-submit"
                        disabled={saving}
                        onClick={submit}
                        className="h-9 px-4 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                    >
                        Revoke key
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
